import classNames from "classnames";
import * as React from "react";
import { useState, useEffect } from "react";
import classes from "./styles.module.scss";
import { useUniqueId } from "Hooks";

export interface LsLoadingProps {
   isLoading: boolean,
   className?: string
}
const getWrapperClass = (loading: boolean): string | undefined => {
   return loading ? `reloading ${classes.reloading}` : undefined;
}

export const useLsLoadingWrapper = (isLoading = false): string | undefined => {
   const [wrapperClass, setWrapperClass] = useState(getWrapperClass(isLoading));

   useEffect(() => {
      setWrapperClass(getWrapperClass(isLoading));
   }, [isLoading])

   return wrapperClass;
}

export const useLsLoading = (initialIsLoading = false): [string | undefined, boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
   const [isLoading, setIsLoading] = useState(initialIsLoading);
   const wrapperClass = useLsLoadingWrapper(isLoading);

   return [wrapperClass, isLoading, setIsLoading];
}

const LsLoading: React.FC<LsLoadingProps> = ({ isLoading, className }) => {
   const gradient1 = useUniqueId("ls-loading-gradient-");
   const gradient2 = useUniqueId("ls-loading-gradient-");
   const gradient3 = useUniqueId("ls-loading-gradient-");
   const gradient4 = useUniqueId("ls-loading-gradient-");
   const gradient5 = useUniqueId("ls-loading-gradient-");

   return (
      <>
         {isLoading
            ? <svg className={classNames(classes.image, className)} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path className={classes.bg} d="M31.444 27.922c-.164 1.717-1.775 3.31-3.511 3.488-7.936.787-15.93.787-23.866 0C2.33 31.232.722 29.64.556 27.922a128.29 128.29 0 0 1 0-23.844C.722 2.361 2.33.776 4.066.59c7.937-.787 15.93-.787 23.867 0 1.736.18 3.347 1.77 3.511 3.488.742 7.93.742 15.913 0 23.844Z" fill="#00293A" />
               <path className={classes.white} d="M.038 12.705c8.293-.003 14.93.004 22.521.004a3.246 3.246 0 0 0 1.797-.557 3.266 3.266 0 0 0 1.44-2.724 3.252 3.252 0 0 0-1.444-2.701 3.25 3.25 0 0 0-1.799-.553l-.02-.002a3.712 3.712 0 0 0-.466.035 3.237 3.237 0 0 0-2.004 1.117 3.296 3.296 0 0 0-.496.79c-2.445 5.393-4.92 10.955-7.094 15.778a3.303 3.303 0 0 1-.498.79 3.231 3.231 0 0 1-2.002 1.117 3.751 3.751 0 0 1-.469.035l-.017-.002a3.25 3.25 0 0 1-1.799-.553 3.27 3.27 0 0 1 1.793-5.982l22.521.003" stroke="#fff" strokeWidth="2.4" />
               <path className={classNames(classes.ln, classes.ln1)} d="M.038 12.705c8.293-.003 14.93.004 22.521.004a3.246 3.246 0 0 0 1.797-.557 3.266 3.266 0 0 0 1.44-2.724 3.252 3.252 0 0 0-1.444-2.701 3.25 3.25 0 0 0-1.799-.553l-.02-.002a3.712 3.712 0 0 0-.466.035 3.237 3.237 0 0 0-2.004 1.117 3.282 3.282 0 0 0-.496.79" stroke={`url(#${gradient1})`} strokeWidth="2.4" />
               <path className={classNames(classes.ln, classes.ln3)} d="m9.481 19.297 22.521.003" stroke={`url(#${gradient2})`} strokeWidth="2.4" />
               <path className={classNames(classes.shadow, classes.shadow2)} d="M9.74 19.3h8.588" stroke={`url(#${gradient3})`} strokeWidth="2.4" />
               <path className={classNames(classes.shadow, classes.shadow1)} d="M13.728 12.712h7" stroke={`url(#${gradient4})`} strokeWidth="2.4" />
               <path className={classNames(classes.ln, classes.ln2)} d="M19.567 8.113c-2.445 5.394-4.92 10.956-7.094 15.78a3.303 3.303 0 0 1-.498.788A3.231 3.231 0 0 1 9.973 25.8a3.751 3.751 0 0 1-.469.035l-.017-.002a3.25 3.25 0 0 1-1.799-.553 3.27 3.27 0 0 1 1.793-5.982" stroke={`url(#${gradient5})`} strokeWidth="2.4" />
               <defs>
                  <linearGradient id={gradient1} x1="-3.031" y1="13.958" x2="19.042" y2="12.249" gradientUnits="userSpaceOnUse">
                     <stop stopColor="#FF8D6B" />
                     <stop offset=".41" stopColor="#FCA197" />
                     <stop offset=".81" stopColor="#F9B5C3" />
                     <stop offset=".91" stopColor="#FBC2A4" />
                     <stop offset="1" stopColor="#FDCF85" />
                  </linearGradient>
                  <linearGradient id={gradient2} x1="9.477" y1="19.235" x2="32.123" y2="19.317" gradientUnits="userSpaceOnUse">
                     <stop offset=".03" stopColor="#ADDFB2" />
                     <stop offset=".109" stopColor="#A1DED2" />
                     <stop offset=".316" stopColor="#519FB7" />
                     <stop offset="1" stopColor="#00609C" />
                  </linearGradient>
                  <linearGradient id={gradient3} x1="10.241" y1="19.198" x2="18.453" y2="19.213" gradientUnits="userSpaceOnUse">
                     <stop stopColor="#A1DED2" stopOpacity="0" />
                     <stop offset=".33" stopColor="#65ADB6" />
                     <stop offset=".48" stopColor="#2A7B9B" />
                     <stop offset=".64" stopColor="#4392AB" />
                     <stop offset="1" stopColor="#5DA9BB" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id={gradient4} x1="14.604" y1="13.008" x2="20.117" y2="13.008" gradientUnits="userSpaceOnUse">
                     <stop stopColor="#FAAEB4" stopOpacity="0" />
                     <stop offset=".26" stopColor="#D18F76" />
                     <stop offset=".53" stopColor="#A97038" />
                     <stop offset=".76" stopColor="#D39F5E" />
                     <stop offset="1" stopColor="#FDCF85" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id={gradient5} x1="9.46" y1="23.717" x2="24.929" y2="11.242" gradientUnits="userSpaceOnUse">
                     <stop stopColor="#A1DED2" />
                     <stop offset=".18" stopColor="#ADDFB2" />
                     <stop offset=".37" stopColor="#BADF93" />
                     <stop offset=".68" stopColor="#DCD78C" />
                     <stop offset="1" stopColor="#FDCF85" />
                  </linearGradient>
               </defs>
            </svg>
            : null}
      </>
   );
}

export default LsLoading;